/**
 * Real light out of a fixture — tier 1 of the lighting work (owner, 2026-10-18:
 * *"we'll go with tier 1"*).
 *
 * The luminaire family has always GLOWED (an emissive diffuser + an additive
 * halo) and never LIT anything: the scene's only lights are the sun, a
 * hemisphere and a small ambient, so a lit lamp brightened its own diffuser and
 * left the room at whatever the sun said. This module is the pure half of the
 * fix — the numbers a `three` light needs, derived from the fixture's own params
 * and the device's live state — so the layer stays a thin wiring pass and the
 * rules are unit-testable without a WebGL context.
 *
 * Deliberate choices:
 * - **Colour temperature, not a glow hue.** A fixture's CCT (`prefs.cct`) is a
 *   design decision; `kelvinToRgb` is the Planckian approximation (Tanner
 *   Helland's fit), clamped to 1000–40000 K. `LUMINAIRE_DEFAULT_CCT` is the
 *   demo house's own warm tone.
 * - **Beam angle per variant.** A spot is 24°, a pendant 60°, a wall sconce 70°,
 *   a bollard 90°; a GLOBE and a STRIP have no cone (`bulb` = a point light,
 *   `strip`/`panel` = a sampled line) — `beamDegFor` returns 0 for those and the
 *   plan says which primitive to use.
 * - **Room-scoped by budget, not by room lookup.** Six real lights is the cap
 *   (`LIGHT_BUDGET`); `pickLightBudget` keeps the ones nearest the camera. A room
 *   test would need the rooms document wired into this layer for the same visual
 *   answer, and the camera is already the operator's own "where am I".
 * - **Intensity is candela** (three r155+ physical lights): the plan's `intensity`
 *   is what a `SpotLight`/`PointLight` takes with `decay = 2`.
 */

import type { LuminaireVariant } from './bimLuminaireSpec';

/** The CCT the demo house's fixtures default to — a warm 3000 K. */
export const LUMINAIRE_DEFAULT_CCT = 3000;

/** The CCTs an operator picks from (K). */
export const CCT_PRESETS = [2200, 2700, 3000, 3500, 4000, 5000, 6500] as const;

/** What a stored CCT is clamped into — below 1800 K nothing reads as white light. */
export const CCT_LIMITS = { min: 1800, max: 8000 } as const;

/** A beam wider than 140° is a point light spelled as a spot; narrower than 8° is a laser. */
export const BEAM_LIMITS = { min: 8, max: 140 } as const;

/** The fixture's own cone, degrees. 0 = not a cone (a line or a globe). */
export const LUMINAIRE_BEAM_DEG: Record<LuminaireVariant, number> = {
  spot: 24,
  pendant: 60,
  wall: 70,
  pole: 90,
  bulb: 0,
  strip: 0,
  panel: 0,
};

/** The cap used when the frame time is unknown (a headless rig, a cold start). */
export const LIGHT_BUDGET = 14;

/**
 * How many fixtures may light their room, from the scene's own frame time.
 *
 * The owner's question (2026-10-18): *"why can we [not] have all lamp turned on?"*
 * — there was no good reason, only a hard cap guessed at 14. A cap belongs where
 * the machine says so: when frames are comfortable there is NO cap (every lit
 * fixture lights), and the budget only sheds when the frame time shows the GPU is
 * struggling. Numbers are whole-frame milliseconds at 60 fps ≈ 16.7.
 */
/**
 * A frame longer than this is a STALL, not a frame rate: the model build, a shader
 * compile, a GC pause and a re-parse all land in the render loop as one
 * multi-second "frame" (measured on this house: 0.3–4.4 s during a load).
 *
 * The distinction matters because the budget is fed a frame-time EMA: a load used
 * to drag that average past 250 ms, which shed the house down to EIGHT lit
 * fixtures, and the recovery when the page settled put them back — the owner's
 * *"the lights flicker and the model gets darkened"* (2026-10-23). A stall says
 * nothing about how fast the page renders, so it must not vote.
 */
export const LIGHT_BUDGET_STALL_MS = 250;

/** Is this frame time a stall rather than a frame rate? */
export function isRenderStall(frameMs: number | null | undefined): boolean {
  return typeof frameMs === 'number' && Number.isFinite(frameMs) && frameMs > LIGHT_BUDGET_STALL_MS;
}

export function adaptiveLightBudget(frameMs: number | null | undefined): number {
  if (typeof frameMs !== 'number' || !Number.isFinite(frameMs) || frameMs <= 0) return LIGHT_BUDGET;
  if (frameMs <= 18) return Number.POSITIVE_INFINITY; // comfortably 60 fps: all of them
  if (frameMs <= 24) return 32; // 40-55 fps: most of a house
  if (frameMs <= 33) return 16; // ~30 fps: a room's worth
  return 8; // below 30 fps: the nearest few that just changed
}

/**
 * How many of those may cast SHADOWS — the expensive half (a map per light, and
 * the model re-drawn into each). The rest still light their room, only without an
 * occluded cone.
 */
/**
 * How many pool spots may carry a shadow map — and it is a HARD ceiling, not taste.
 *
 * Owner, 2026-10-21: *"can we try all lamps the same.. no limits.."*. Tried at 48: the
 * model stopped rendering entirely. A shadow-casting light costs one texture unit in the
 * fragment shader, and a GPU exposes about sixteen of them (WebGL2's floor); the rest of
 * the budget goes on the model's own maps. Over the limit the shader fails to compile and
 * NOTHING draws — a blank scene, not a dimmer one.
 *
 * 12 leaves headroom for the model's textures and the shadow samplers three reserves.
 * A lamp outside the twelve does not occlude, which is why a pool can still be seen
 * crossing a wall; the fix for that is not a bigger constant, it is baked or cascaded
 * shadows, which is a different piece of work.
 */
export const SHADOW_BUDGET = 12;

/** A strip longer than this is sampled this many times (1 sample ≈ 0.6 m). */
export const STRIP_SAMPLE_SPACING_M = 0.6;
export const STRIP_MAX_SAMPLES = 6;

/** Candela at full brightness, per fixture kind — tuned against the night scene. */
const CANDELA: Record<LuminaireVariant, number> = {
  // Tuned DOWN twice. The first pass was sized for a hall; the second was still too
  // hot for a house (owner, 2026-10-21: *"all lights are too bright"*). A domestic
  // downlight is a few hundred lumens over a small cone, which at 2.7 m is roughly the
  // hundred candela these now claim — not the 260 the last pass used.
  //
  // A fixture with an explicit `prefs.lux` ignores this table entirely and is solved
  // from the target illuminance instead (`fixtureLuxCandela`), which is the control an
  // operator can actually reason about.
  spot: 120,
  pendant: 80,
  wall: 55,
  pole: 40,
  bulb: 55,
  strip: 28,
  panel: 20,
};

/**
 * Candela needed to put `lux` on the floor `heightM` below a fixture, by the inverse-square
 * law (E = I / d²). Clamped, because a 5000 lx request from a 0.4 m ceiling would otherwise
 * ask for a sun inside the room.
 */
export function fixtureLuxCandela(lux: number, heightM: number): number {
  const target = Number.isFinite(lux) ? Math.min(4000, Math.max(1, lux)) : 0;
  const height = Number.isFinite(heightM) ? Math.min(12, Math.max(0.5, heightM)) : 2.7;
  return Math.round(Math.min(6000, Math.max(2, target * height * height)));
}

/**
 * How much of the CCT's own hue survives into the cone. 1 = the Planckian colour
 * as computed (which reads almost white through ACES), >1 pushes the channels
 * apart so 2700 K pools warm and 6500 K pools cool — the owner's *"could the cone
 * contain color, color temperature"*.
 */
const TINT_STRENGTH = 1.55;

/** The CCT drawn as NEUTRAL — the reference `fixtureColor` tints away from. */
export const NEUTRAL_CCT = 4000;

/** The light's own reach, metres (a cutoff, not a falloff — decay 2 does that). */
const REACH_M = 12;

/** A straight line variant: emitted along its length, never down a cone. */
export function isLinearVariant(variant: LuminaireVariant): boolean {
  return variant === 'strip' || variant === 'panel';
}

/** The beam a fixture uses: the stored one when it is a number, else its variant's. */
export function beamDegFor(variant: LuminaireVariant, stored?: number | null): number {
  if (isLinearVariant(variant) || variant === 'bulb') return 0;
  const base = LUMINAIRE_BEAM_DEG[variant] ?? 24;
  if (typeof stored !== 'number' || !Number.isFinite(stored)) return base;
  return Math.min(BEAM_LIMITS.max, Math.max(BEAM_LIMITS.min, stored));
}

/** A stored CCT, clamped; anything unusable reads as the house default. */
export function clampCct(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : LUMINAIRE_DEFAULT_CCT;
  return Math.round(Math.min(CCT_LIMITS.max, Math.max(CCT_LIMITS.min, n)));
}

/**
 * Colour temperature → linear-ish RGB 0..1 (Tanner Helland's fit).
 *
 * Good enough to tell 2700 K from 4000 K from 6500 K on a wall, which is the
 * whole job here; not a spectral renderer.
 */
export function kelvinToRgb(kelvin: number): [number, number, number] {
  const t = Math.min(40000, Math.max(1000, Number.isFinite(kelvin) ? kelvin : LUMINAIRE_DEFAULT_CCT)) / 100;
  let r: number;
  let g: number;
  let b: number;

  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
    b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
    b = 255;
  }

  const norm = (v: number) => Math.min(255, Math.max(0, v)) / 255;
  return [norm(r), norm(g), norm(b)];
}

/**
 * The colour a fixture's light is DRAWN in — its CCT with the SATURATION pushed
 * out, so the cone and its pool read warm or cool instead of white.
 *
 * The first version pushed each channel away from 0.5 and clamped; at 3000 K that
 * turned (1, .83, .66) into (1, 1, .75) — a 6 % cast the tone mapping erases
 * (owner, 2026-10-18: *"but what about color of the cone?"*). Saturation in HSL
 * cannot clip: the hue is kept, only its distance from grey grows.
 */
export function fixtureColor(cct: number): [number, number, number] {
  // Relative to a NEUTRAL reference, not to white: the Planckian fit is whitest at
  // ~6600 K, so 6500 K is "white" in absolute terms and boosting it does nothing.
  // Dividing by the reference's own colour makes below-4000 K warm and above it
  // cool, which is what the operator means by the temperature.
  const [r, g, b] = kelvinToRgb(cct);
  const [nr, ng, nb] = kelvinToRgb(NEUTRAL_CCT);
  const relative: [number, number, number] = [r / nr, g / ng, b / nb];
  const peak = Math.max(relative[0], relative[1], relative[2]) || 1;
  const normalised: [number, number, number] = [relative[0] / peak, relative[1] / peak, relative[2] / peak];
  const [h, s, l] = rgbToHsl(normalised[0], normalised[1], normalised[2]);
  return hslToRgb(h, Math.min(1, s * TINT_STRENGTH), l);
}

/** RGB (0…1) → HSL (h 0…1, s 0…1, l 0…1). */
export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

/** HSL → RGB, the inverse of `rgbToHsl`. */
export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    let value = t;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    if (value < 1 / 6) return p + (q - p) * 6 * value;
    if (value < 1 / 2) return q;
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)];
}

/**
 * The colour the CONE is drawn in, where a device may report its own RGB.
 *
 * A hue-style light carries `colour` on the hub and the fixture's diffuser and
 * halo have always taken it — but the light the cone casts took the fixture's CCT,
 * so a lamp set to red glowed red and painted a 3000 K white pool (owner,
 * 2026-10-18: *"but for color lights there is no cone"*). A device colour that is
 * actually a colour wins; a white-ish report means a tunable-white lamp and falls
 * back to its CCT. Saturation gets the same boost as the CCT tint, so a red reads
 * red on a wall instead of pink-white.
 */
export function fixtureLightColor(input: {
  device: [number, number, number] | null;
  cct: number;
}): [number, number, number] {
  const device = input.device;
  if (!device) return fixtureColor(input.cct);
  const [r, g, b] = device;
  // HSL saturation is the wrong white test: near white it divides by a tiny
  // denominator and reports ~1.0 for (1, .99, .98). The CHANNEL SPREAD is honest.
  const spread = Math.max(r, g, b) - Math.min(r, g, b);
  if (spread < 0.06) return fixtureColor(input.cct);
  const [h, s, l] = rgbToHsl(r, g, b);
  return hslToRgb(h, Math.min(1, s * TINT_STRENGTH), l);
}

/**
 * The VISIBLE cone: the shaft of light in the air, as geometry.
 *
 * Owner, 2026-10-18: *"all lights should show the cone.. otherwise its not good"*
 * — a pool on the floor with nothing above it reads as a stain, and the budget
 * must never hide the cone (it caps the lights that LIGHT, not the ones that
 * SHOW). One additive open cone per lit fixture: cheap (no shadow map, no
 * per-light shading), and its radius is the fixture's own beam.
 */
export interface FixtureCone {
  /** Half-angle the cone is drawn at, radians (a little wider than the light). */
  angleRad: number;
  /** How far it is drawn, metres — the light's reach, capped so it stays in the room. */
  lengthM: number;
  /** Opacity at full brightness, before the fixture's own dimming. */
  opacity: number;
}

/** A cone a little wider than the light itself: the shaft is a volume, not an edge. */
const CONE_WIDENING = 1.18;

/** Beyond this a shaft is a searchlight, not a fixture. */
const CONE_MAX_M = 6;

export function fixtureCone(input: { angleRad: number; distance: number; brightness: number; variant: LuminaireVariant }): FixtureCone | null {
  const brightness = Math.min(1, Math.max(0, input.brightness));
  if (brightness <= 0) return null;
  // A globe and a line have no cone to draw — a sphere of light is the halo's job.
  if (input.variant === 'bulb' || isLinearVariant(input.variant)) return null;
  return {
    angleRad: Math.min(Math.PI / 2.6, input.angleRad * CONE_WIDENING),
    lengthM: Math.min(CONE_MAX_M, Math.max(0.6, input.distance * 0.45)),
    opacity: 0.05 + 0.13 * brightness,
  };
}

/** What a fixture's live state asks the scene to draw. */
export type FixtureLightPlan =
  | { kind: 'none' }
  | { kind: 'spot'; intensity: number; angleRad: number; penumbra: number; distance: number }
  | { kind: 'point'; intensity: number; distance: number }
  | { kind: 'linear'; intensity: number; distance: number; samples: number };

/**
 * The light a fixture contributes at a given brightness (0…1).
 *
 * `none` is a real answer: an off light, a brightness the hub did not report, or
 * a variant with no light of its own. Everything else carries the numbers a
 * `three` light takes, so the layer never invents them.
 */
export function fixtureLightPlan(input: {
  variant: LuminaireVariant;
  beamDeg: number;
  brightness: number;
  lengthM?: number;
}): FixtureLightPlan {
  const brightness = Math.min(1, Math.max(0, Number.isFinite(input.brightness) ? input.brightness : 0));
  if (brightness <= 0) return { kind: 'none' };
  const base = CANDELA[input.variant] ?? 300;
  const intensity = base * brightness;

  if (isLinearVariant(input.variant)) {
    const samples = stripSampleCount(input.lengthM ?? 1);
    return { kind: 'linear', intensity: intensity / samples, distance: REACH_M, samples };
  }
  if (input.variant === 'bulb') return { kind: 'point', intensity, distance: REACH_M };

  // A spot needs a cone: half-angle, and a penumbra soft enough that the edge is
  // not a cut-out at domestic distances.
  const angleRad = (Math.max(BEAM_LIMITS.min, Math.min(BEAM_LIMITS.max, input.beamDeg)) * Math.PI) / 360;
  // A SHARP-edged cone reads as a flashlight (owner: *"too sharp"*); 0.85 is the
  // wide soft falloff a domestic fixture actually has.
  return { kind: 'spot', intensity, angleRad, penumbra: 0.85, distance: REACH_M };
}

/** How many point lights a strip of this length is sampled with. */
export function stripSampleCount(lengthM: number): number {
  const length = Number.isFinite(lengthM) && lengthM > 0 ? lengthM : 1;
  return Math.min(STRIP_MAX_SAMPLES, Math.max(1, Math.round(length / STRIP_SAMPLE_SPACING_M)));
}

/** Evenly spaced offsets along a strip's axis, centred on 0 (metres). */
export function stripSampleOffsets(lengthM: number, samples = stripSampleCount(lengthM)): number[] {
  const length = Number.isFinite(lengthM) && lengthM > 0 ? lengthM : 1;
  if (samples <= 1) return [0];
  const step = length / samples;
  return Array.from({ length: samples }, (_, i) => -length / 2 + step / 2 + i * step);
}

/**
 * Which fixtures get a real light, given the camera: the nearest `budget` ones.
 * Pure, so the rule is testable without a scene — and stable (sorted by distance,
 * then key) so two fixtures at the same distance do not swap every frame.
 */
/** Which way a fixture's cone points (owner, 2026-10-21). */
export type FixtureAim = 'down' | 'up' | 'out' | 'back';

/**
 * The unit vector a fixture throws along.
 *
 * The wall's own normal is the reference for a mounted fixture: `out` is into the room (the
 * behaviour before this existed), `back` is toward the wall. `down`/`up` are vertical
 * regardless of the mount, and a fixture with no wall plane takes vertical for `out`/`back`
 * too — "out" from a ceiling is down.
 */
export function fixtureAimVector(
  aim: FixtureAim | null | undefined,
  normal: readonly [number, number, number] | null,
  hasCone: boolean,
): [number, number, number] {
  const wall: [number, number, number] = normal
    ? [normal[0], normal[1], normal[2]]
    : [0, -1, 0];
  const length = Math.hypot(wall[0], wall[1], wall[2]);
  const unit: [number, number, number] = length > 1e-9
    ? [wall[0] / length, wall[1] / length, wall[2] / length]
    : [0, -1, 0];

  if (aim === 'down') return [0, -1, 0];
  if (aim === 'up') return [0, 1, 0];
  if (aim === 'out') return unit;
  // `+ 0` folds -0 into 0: a vector of negative zeros compares unequal to [0, 0, -1] under
  // a strict deep-equal, which is a test failing on a sign that means nothing.
  if (aim === 'back') return [0 - unit[0], 0 - unit[1], 0 - unit[2]];
  // Auto: a cone on a wall throws into the room, everything else shines down.
  return hasCone && normal ? unit : [0, -1, 0];
}

/** What the ordering rule needs to know about one fixture. */
export interface ShadowPreference {
  /** `true` = always cast, `false` = never, `null` = auto (fill what is left). */
  shadowPref: boolean | null;
  /** A grazing (wall) fixture: allowed, but kept out of the cast slots when possible. */
  grazing: boolean;
}

/**
 * Which fixtures get the shadow slots.
 *
 * `prefs.shadow` is the operator's call (owner, 2026-10-21: *"per lamp setting which
 * gets the real shadow and which not"*), so an explicit `true` outranks everything and
 * an explicit `false` may never take a slot. `null` — no opinion — fills the slots the
 * explicit choices leave, non-grazing first, because a wall sconce's map starts on a
 * wall plane and is the likeliest to show acne.
 */
export function orderForShadowSlots<T extends ShadowPreference>(entries: T[]): T[] {
  const rank = (entry: T): number => {
    if (entry.shadowPref === true) return entry.grazing ? 1 : 0;
    if (entry.shadowPref === null) return entry.grazing ? 3 : 2;
    return 4;
  };
  return [...entries]
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => rank(a.entry) - rank(b.entry) || a.index - b.index)
    .map((wrapped) => wrapped.entry);
}

/** A room's own intensity preset — the operator's three words. */
export type LightPreset = 'low' | 'mid' | 'high';

/**
 * How much of a fixture's candela a preset allows. `mid` is the calibrated 1.0 the
 * numbers in `CANDELA` were tuned to; `low` is a night scene, `high` a work light.
 */
export const LIGHT_PRESET_FACTOR: Record<LightPreset, number> = { low: 0.5, mid: 1, high: 1.7 };

/** A stored word, or the house default. */
export function lightPresetOf(value: unknown): LightPreset {
  return value === 'low' || value === 'high' ? value : 'mid';
}

/** A fixture touched this recently counts as "just switched" by the budget. */
export const LIGHT_RECENT_MS = 60_000;

/**
 * Which fixtures get a real light — chosen by the fixtures' OWN state, never by
 * where the camera is.
 *
 * Two reports shaped this. First distance alone (*"you turn one light and surface
 * from another shows"*): switching on a lamp outside the nearest set re-picked the
 * set. Then the same rule again, seen from the other end (*"greenhouse is lit…
 * i move view to living room… greenhouse seems to turn off"*): a camera-driven
 * budget makes a lamp's light depend on the operator's viewpoint, so walking
 * across the house switches lights off behind you. A light is a fact about the
 * lamp, not about the camera.
 *
 * So: the fixtures touched most recently hold slots first, and the tiebreak is the
 * KEY (stable), not a distance. `budget` caps how many light at once; beyond it a
 * lamp keeps its glow (the cone and the emissive are never budgeted) but stops
 * lighting the room.
 */
export function pickLightBudget(
  lights: ReadonlyArray<{ key: string; touchedAt?: number }>,
  budget = LIGHT_BUDGET,
): string[] {
  return [...lights]
    .sort((a, b) => (b.touchedAt ?? 0) - (a.touchedAt ?? 0) || a.key.localeCompare(b.key))
    .slice(0, Math.max(0, budget))
    .map((light) => light.key);
}
